/**
 * One shape for everything on the year that has a date.
 *
 * `Year.tsx` used to hold six kinds of thing as six separate arrays and merge
 * them by hand inside `PeriodCard` — commitments, flights, holidays and
 * "today" each got their own `rows.push`. WP 4.7 adds birthdays, dated tasks
 * and tentative saved events to the pile, which is the point at which "each
 * caller merges its own six" stops being maintainable and becomes the thing
 * CLAUDE.md warns about: the merge logic itself, duplicated at every call
 * site, is the bug waiting to happen. So it is done once, here, and every
 * caller reads the same list — `MonthRibbon.tsx` places what comes out of
 * here inside a `PeriodCard`'s own date-range slice (WP 6.5, replacing the
 * WP 5.6 rail). A visitor is the one exception: see `visitorItems`, below,
 * for why it is a separate function rather than a kind `yearItems()` itself
 * produces.
 *
 * Two imports, `ics.ts`'s clock and `zone.ts`'s zones, spelled with `.ts`:
 * this is loaded directly by the root suite's Node test runner, which will
 * not resolve an extensionless specifier the way Vite does, and both are
 * dependency-free themselves. Every shape below is duck-typed rather than
 * imported from `types.ts`.
 */
import { timeIn } from './ics.ts'
import { dayIn, hereZone, zoneOfCity } from './zone.ts'


import { routeLabel } from './flightRoute.ts'

export type YearItemKind = 'commitment' | 'flight' | 'birthday' | 'holiday' | 'task' | 'saved' | 'visitor'

export interface YearItem {
  id: string
  kind: YearItemKind
  /** ISO date, `YYYY-MM-DD`. */
  on: string
  /** `HH:MM`, where the thing has a time and not just a day. */
  time?: string
  title: string
  fact?: string
  /** A saved event with one showing and no commitment yet — shown as an
   *  outlined dot and a `Tag` reading "saved" rather than a filled one,
   *  until the Shortlist's "pick a night" turns it into a real commitment. */
  tentative?: boolean
  ref: { table: string; id: string }
}

interface CommitmentLike {
  id: string
  on_date: string
  title: string
  /** The exact moment a showing chip picked (WP 3.3); absent on a plain day. */
  starts_at?: string | null
}

interface FlightLike {
  id: string
  on_date: string
  depart_at?: string | null
  from_iata?: string | null
  to_iata?: string | null
  code?: string | null
}

interface HolidayLike {
  /** ISO date. */
  on: string
  name: string
  /** 'DE' or 'IE', for the fact line ("DE holiday" / "IE holiday"). */
  country: 'DE' | 'IE'
}

interface TaskLike {
  id: string
  body: string
  due_on: string | null
}

/** Structurally what `ShowingLite`/`ShowingRow` both already satisfy —
 *  `rowMeta.ts` and `today.ts` keep the same small copy for the same reason. */
export interface Showing {
  starts_at: string
}

interface SavedRowLike {
  id: string
  title: string
  /** The row's town, whose region's zone its showing is read in (C2.1). */
  city?: string | null
}

/** Structurally what `Visitor` (`types.ts`) satisfies. */
interface VisitorLike {
  id: string
  who: string
  starts_on: string
  ends_on: string
}

export interface YearItemsInput {
  commitments: CommitmentLike[]
  flights: FlightLike[]
  birthdays: CommitmentLike[]
  holidays: HolidayLike[]
  tasks: TaskLike[]
  /** Already resolved to a single showing per entry — see `pickTentativeSaved`. */
  saved: { row: SavedRowLike; showing: Showing }[]
}

const inYear = (isoDate: string, year: number) => isoDate.slice(0, 4) === String(year)
/** Wall-clock in `tz`, not `slice(11, 16)`'s UTC (a3-02); undefined for a
 *  bare midnight, which is a day with no time on it. A commitment or a flight
 *  is the person's own and reads in the current region's zone; a saved
 *  event's showing in its own town's (C2.1). */
const timeOf = (isoInstant: string, tz: string) => timeIn(isoInstant, tz) ?? undefined
/** A day count with no `calendar.ts` import — see this file's own header for
 *  why: the root suite loads it directly and duck-types every shape. */
const nightsOf = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000)

export function yearItems(input: YearItemsInput, year: number): YearItem[] {
  const items: YearItem[] = []

  for (const c of input.commitments) {
    if (!inYear(c.on_date, year)) continue
    items.push({
      id: `commitment:${c.id}`, kind: 'commitment', on: c.on_date,
      time: c.starts_at ? timeOf(c.starts_at, hereZone()) : undefined,
      title: c.title,
      ref: { table: 'commitments', id: c.id },
    })
  }

  for (const f of input.flights) {
    if (!inYear(f.on_date, year)) continue
    const time = f.depart_at ? f.depart_at.slice(0, 5) : undefined
    items.push({
      id: `flight:${f.id}`, kind: 'flight', on: f.on_date, time,
      // WP 10.7 (10.5's own follow-up, confirmed by 10.6): the fact line
      // used to read "flight FR5419 17:20" under a title that already read
      // "FR5419 · BER → DUB" — the code twice, and "flight" restating what
      // the row's own plane-icon plate already says. The code stays where
      // it was said first (the title); the fact line's one job now is the
      // one thing nothing else on the row states: the route.
      title: f.code ?? 'Flight',
      fact: routeLabel(f.from_iata, f.to_iata),
      ref: { table: 'flights', id: f.id },
    })
  }

  for (const b of input.birthdays) {
    if (!inYear(b.on_date, year)) continue
    items.push({
      id: `birthday:${b.id}`, kind: 'birthday', on: b.on_date, title: b.title,
      ref: { table: 'commitments', id: b.id },
    })
  }

  for (const h of input.holidays) {
    if (!inYear(h.on, year)) continue
    items.push({
      id: `holiday:${h.on}:${h.name}`, kind: 'holiday', on: h.on, title: h.name,
      fact: `${h.country} holiday`,
      ref: { table: 'holiday', id: h.on },
    })
  }

  for (const t of input.tasks) {
    if (!t.due_on || !inYear(t.due_on, year)) continue
    items.push({
      id: `task:${t.id}`, kind: 'task', on: t.due_on, title: t.body, fact: 'Task',
      ref: { table: 'list_items', id: t.id },
    })
  }

  for (const s of input.saved) {
    const tz = zoneOfCity(s.row.city)
    const on = dayIn(s.showing.starts_at, tz)
    if (!inYear(on, year)) continue
    items.push({
      id: `saved:${s.row.id}`, kind: 'saved', on, time: timeOf(s.showing.starts_at, tz),
      title: s.row.title, fact: 'saved', tentative: true,
      ref: { table: 'entries', id: s.row.id },
    })
  }

  return items.sort((a, b) => (a.on !== b.on ? (a.on < b.on ? -1 : 1) : (a.time ?? '').localeCompare(b.time ?? '')))
}

/**
 * A visitor, as year items — kept out of `yearItems()` itself and its
 * `YearItemsInput`, on purpose. `yearItems()`'s whole output feeds a
 * `PeriodCard`'s date-range item slice as well as Today's Coming up, and
 * folding a visitor's own item into that one pool was the one-value-two-jobs
 * bug this file's header names: Today's Coming up wants a "Rachel visiting"
 * line, but a `PeriodCard` already has nothing to do with a visitor — the
 * Year draws one as its own `VisitorCard`, beside period cards rather than
 * inside one — and a blind date-range filter over the combined pool cannot
 * tell the two apart. So a visitor is a year item only for a caller that
 * asks for it here, separately; `Year.tsx` never calls this, `Today.tsx`
 * does (WP 6.2). One item per visit, on the arrival day only — a departure
 * day carries nothing of its own to say.
 */
export function visitorItems(visitors: VisitorLike[], year: number): YearItem[] {
  const items: YearItem[] = []
  for (const v of visitors) {
    if (!inYear(v.starts_on, year)) continue
    const n = nightsOf(v.starts_on, v.ends_on)
    items.push({
      id: `visitor:${v.id}`, kind: 'visitor', on: v.starts_on,
      title: `${v.who} visiting`, fact: `${n} night${n === 1 ? '' : 's'}`,
      ref: { table: 'visitors', id: v.id },
    })
  }
  return items.sort((a, b) => (a.on !== b.on ? (a.on < b.on ? -1 : 1) : 0))
}

/**
 * Rule 3: a saved event is a year item only once it has exactly one future
 * showing and nothing has scheduled it yet. Two showings is a choice still
 * to make — that is the Shortlist's "pick a night" — and a row already
 * carrying a commitment already has its date, as a `commitment` item, so
 * counting it again here would be the one-value-two-jobs bug: the same
 * evening would draw two dots.
 *
 * Exported and tested on its own so the "three showings → absent" rule is
 * checked directly, rather than folded into `yearItems`'s fixed signature,
 * which only ever takes an already-singular `showing`.
 */
export function pickTentativeSaved(
  entries: { row: SavedRowLike; showings: Showing[] }[],
  scheduledEntryIds: ReadonlySet<string> = new Set(),
): { row: SavedRowLike; showing: Showing }[] {
  const out: { row: SavedRowLike; showing: Showing }[] = []
  for (const e of entries) {
    if (scheduledEntryIds.has(e.row.id)) continue
    if (e.showings.length !== 1) continue
    out.push({ row: e.row, showing: e.showings[0] })
  }
  return out
}
